import axiosInstance from './axios/index';

export interface InspectionRating {
  aiNotes: string | null;
  aiRating: string | null;
  aiScore: number | null;
  id: string;
  overrideRating: string | null;
  overrideScore: number | null;
}

export interface InspectionPhoto {
  areaNameSnapshot?: string | null;
  areaTypeSnapshot?: string | null;
  blobUrl: string;
  id: string;
  projectAreaId?: string | null;
  promptText: string | null;
  rating?: InspectionRating;
  submittedAt: string;
}

export interface Inspection {
  assignedTo: { firstName: string; id: string; lastName: string };
  createdAt: string;
  dueBy: string | null;
  escalated: boolean;
  id: string;
  photos: InspectionPhoto[];
  project: {
    id: string;
    proposal?: { clientName?: string; lead?: { name?: string } };
  };
  projectAreaId: string | null;
  promptText?: string;
  ratedAt: string | null;
  status: string;
  submittedAt: string | null;
  type: 'baseline' | 'baseline_calibration' | 'manual' | 'surprise';
}

export interface TechInspectionHistory {
  inspections: Inspection[];
  rollingAvg: number | null;
}

export interface ProjectScoreSummary {
  avgScore: number | null;
  lastScore: number | null;
}

export interface InspectionSettings {
  abeInspectEnabled: boolean;
  inspectActiveHoursEnd: string | null;
  inspectActiveHoursStart: string | null;
  inspectLotteryWeight: number;
  inspectScoreVisibleToClient: boolean;
}

// ── Operator ──────────────────────────────────────────────────────────────────

export const getAllInspections = async (): Promise<Inspection[]> => {
  const response = await axiosInstance.get('/inspections');
  return response.data;
};

export const getInspectionsByProject = async (
  projectId: string
): Promise<Inspection[]> => {
  const response = await axiosInstance.get(`/inspections/project/${projectId}`);
  return response.data;
};

export const getProjectInspectionScore = async (
  projectId: string
): Promise<ProjectScoreSummary> => {
  const response = await axiosInstance.get(
    `/inspections/project/${projectId}/score`
  );
  return response.data;
};

export const getInspectionById = async (id: string): Promise<Inspection> => {
  const response = await axiosInstance.get(`/inspections/${id}`);
  return response.data;
};

export const createInspection = async (data: {
  assignedToId: string;
  projectAreaId?: string;
  projectId: string;
  type: 'baseline' | 'baseline_calibration' | 'manual' | 'surprise';
  visitId?: string;
}): Promise<Inspection> => {
  const response = await axiosInstance.post('/inspections', data);
  return response.data;
};

export const overrideRating = async (
  inspectionId: string,
  photoId: string,
  data: { overrideRating: string; overrideScore: number }
): Promise<void> => {
  await axiosInstance.patch(
    `/inspections/${inspectionId}/photos/${photoId}/override`,
    data
  );
};

// ── Tech ──────────────────────────────────────────────────────────────────────

export const getTechActiveInspection = async (): Promise<Inspection | null> => {
  const response = await axiosInstance.get('/inspections/tech/active');
  return response.data;
};

export const getTechActiveManualInspection =
  async (): Promise<Inspection | null> => {
    const response = await axiosInstance.get('/inspections/tech/active-manual');
    return response.data;
  };

export const getTechInspectionHistory =
  async (): Promise<TechInspectionHistory> => {
    const response = await axiosInstance.get('/inspections/tech/history');
    return response.data;
  };

export const submitInspectionPhoto = async (
  inspectionId: string,
  data: { blobUrl: string; projectAreaId?: string; promptText?: string }
): Promise<InspectionPhoto> => {
  const response = await axiosInstance.post(
    `/inspections/tech/${inspectionId}/submit`,
    data
  );
  return response.data;
};

// ── Settings ──────────────────────────────────────────────────────────────────

export const getInspectionSettings = async (): Promise<InspectionSettings> => {
  const response = await axiosInstance.get('/inspections/settings');
  return response.data;
};

export const updateInspectionSettings = async (
  data: Partial<InspectionSettings>
): Promise<InspectionSettings> => {
  const response = await axiosInstance.patch('/inspections/settings', data);
  return response.data;
};

// ── Project Areas ─────────────────────────────────────────────────────────────

export interface ProjectArea {
  areaType?: string;
  companyId: string;
  createdAt: string;
  id: string;
  name: string;
  orderIndex: number;
}

export interface ProjectBaselineStatus {
  areaCount: number;
  hasAreas: boolean;
  hasBaseline: boolean;
  qualifiedAreaCount: number;
}

export const getProjectAreas = async (
  projectId: string
): Promise<ProjectArea[]> => {
  const response = await axiosInstance.get(
    `/project-areas/project/${projectId}`
  );
  return response.data;
};

export const createProjectArea = async (
  projectId: string,
  data: { areaType?: string; name: string; orderIndex?: number }
): Promise<ProjectArea> => {
  const response = await axiosInstance.post(
    `/project-areas/project/${projectId}`,
    data
  );
  return response.data;
};

export const updateProjectArea = async (
  id: string,
  data: { areaType?: string; name?: string; orderIndex?: number }
): Promise<void> => {
  await axiosInstance.patch(`/project-areas/${id}`, data);
};

export const deleteProjectArea = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/project-areas/${id}`);
};

export const getProjectBaselineStatus = async (
  projectId: string
): Promise<ProjectBaselineStatus> => {
  const response = await axiosInstance.get(
    `/inspections/project/${projectId}/baseline-status`
  );
  return response.data;
};

export interface AreaBaseline {
  areaId: string;
  areaName: string;
  areaType: string | null;
  avgScore: number | null;
  confidenceLevel: number;
  orderIndex: number;
  photoCount: number;
}

export const getAreasBaseline = async (
  projectId: string
): Promise<AreaBaseline[]> => {
  const response = await axiosInstance.get(
    `/inspections/project/${projectId}/areas-baseline`
  );
  return response.data;
};

export const completeBaseline = async (inspectionId: string): Promise<Inspection> => {
  const response = await axiosInstance.post(`/inspections/${inspectionId}/complete-baseline`);
  return response.data;
};

export const assignPhotoToArea = async (
  photoId: string,
  projectAreaId: string | null
): Promise<void> => {
  await axiosInstance.patch(`/inspections/photos/${photoId}/area`, { projectAreaId });
};

export const deleteInspectionPhoto = async (photoId: string): Promise<void> => {
  await axiosInstance.delete(`/inspections/photos/${photoId}`);
};
